import { Component, EventEmitter, Input, Output, signal, inject } from '@angular/core';
import { CommonModule, CurrencyPipe } from '@angular/common';
import { Router } from '@angular/router';

// PrimeNG
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DividerModule } from 'primeng/divider';
import { LucideAngularModule, CircleCheck, ArrowRight, ArrowUpRight } from 'lucide-angular';

export interface UpgradeDialogData {
  tipoLimite: 'USUARIOS' | 'EQUIPOS' | 'CONTRATOS' | 'STORAGE' | 'FEATURE';
  valorActual: number;
  valorMaximo: number;
  planActual: string;
  mensaje?: string;
}

interface PlanRecomendado {
  codigo: string;
  nombre: string;
  precio: number;
  limite: number;
  features: string[];
}

@Component({
  selector: 'app-upgrade-plan-dialog',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ButtonModule,
    DividerModule,
    CurrencyPipe,
    LucideAngularModule
  ],
  template: `
    <p-dialog 
      [(visible)]="visible" 
      [modal]="true" 
      [style]="{width: '500px'}"
      [draggable]="false"
      [closable]="true"
      (onHide)="onClose()"
      [header]="getHeader()">
      
      <div class="upgrade-content">
        <!-- Icono y mensaje principal -->
        <div class="limit-alert">
          <div class="alert-icon" [class]="getIconClass()">
            <i [class]="getIcon()"></i>
          </div>
          <h3>{{ getTitulo() }}</h3>
          <p>{{ getMensaje() }}</p>
        </div>

        <!-- Barra de uso -->
        <div class="usage-display">
          <div class="usage-labels">
            <span>Uso actual</span>
            <span class="usage-numbers">{{ data?.valorActual || 0 }} / {{ data?.valorMaximo || 0 }}</span>
          </div>
          <div class="usage-bar">
            <div class="usage-fill" [style.width.%]="100"></div>
          </div>
        </div>

        <p-divider></p-divider>

        <!-- Plan recomendado -->
        <div class="recommended-plan">
          <h4>Plan Recomendado</h4>
          
          <div class="plan-card">
            <div class="plan-header">
              <div class="plan-name">
                <span class="badge">Recomendado</span>
                <h3>{{ planRecomendado().nombre }}</h3>
              </div>
              <div class="plan-price">
                <span class="price">{{ planRecomendado().precio | currency:'CLP':'symbol-narrow':'1.0-0' }}</span>
                <span class="period">/mes</span>
              </div>
            </div>

            <div class="plan-features">
              @for (feature of planRecomendado().features; track feature) {
                <div class="feature">
                  <lucide-angular [img]="icons.CircleCheck" [size]="15"></lucide-angular>
                  <span>{{ feature }}</span>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Comparación rápida -->
        <div class="comparison">
          <div class="comparison-item current">
            <span class="label">{{ data?.planActual || 'Tu plan' }}</span>
            <span class="value">{{ data?.valorMaximo }} {{ getLimiteLabel() }}</span>
          </div>
          <lucide-angular [img]="icons.ArrowRight" [size]="16"></lucide-angular>
          <div class="comparison-item upgrade">
            <span class="label">{{ planRecomendado().nombre }}</span>
            <span class="value">{{ planRecomendado().limite }} {{ getLimiteLabel() }}</span>
          </div>
        </div>
      </div>

      <ng-template pTemplate="footer">
        <div class="dialog-footer">
          <button pButton label="Quizás después" [outlined]="true"
                  (click)="onClose()"></button>
          <button pButton (click)="onUpgrade()">
            <lucide-angular [img]="icons.ArrowUpRight" [size]="15"></lucide-angular> <span>Ver Planes</span>
          </button>
        </div>
      </ng-template>
    </p-dialog>
  `,
  styles: [`
    .upgrade-content {
      padding: 0.5rem 0;
    }

    .limit-alert {
      text-align: center;
      padding: 1rem 0;

      .alert-icon {
        width: 70px;
        height: 70px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 1rem;

        i {
          font-size: var(--fs-3xl);
          color: white;
        }

        &.usuarios { background: linear-gradient(135deg, var(--kpi-accent), var(--orange-900)); }
        &.equipos { background: linear-gradient(135deg, var(--kpi-warning), var(--warning-color)); }
        &.contratos { background: linear-gradient(135deg, var(--success-color), var(--green-700)); }
        &.storage { background: linear-gradient(135deg, var(--purple-500), var(--purple-700)); }
        &.feature { background: linear-gradient(135deg, var(--pink-500, #ec4899), var(--pink-700, #be185d)); }
      }

      h3 {
        margin: 0 0 0.5rem 0;
        font-size: var(--type-stat-fs);
      }

      p {
        margin: 0;
        color: var(--text-color-secondary);
      }
    }

    .usage-display {
      background: var(--surface-ground);
      padding: 1rem;
      border-radius: var(--radius-lg);

      .usage-labels {
        display: flex;
        justify-content: space-between;
        margin-bottom: 0.5rem;
        font-size: var(--fs-sm);

        .usage-numbers {
          font-weight: var(--fw-semibold);
          color: var(--danger);
        }
      }

      .usage-bar {
        height: 10px;
        background: var(--surface-300);
        border-radius: var(--radius-md);
        overflow: hidden;

        .usage-fill {
          height: 100%;
          background: linear-gradient(90deg, var(--accent), var(--danger));
          border-radius: var(--radius-md);
        }
      }
    }

    .recommended-plan {
      h4 {
        margin: 0 0 1rem 0;
        font-size: var(--fs-base);
        color: var(--text-color-secondary);
      }
    }

    .plan-card {
      border: 2px solid var(--primary-color);
      border-radius: var(--radius-xl);
      overflow: hidden;

      .plan-header {
        background: var(--primary-color);
        color: white;
        padding: 1rem;
        display: flex;
        justify-content: space-between;
        align-items: center;

        .plan-name {
          .badge {
            background: rgba(255,255,255,0.2);
            padding: 0.25rem 0.5rem;
            border-radius: var(--radius-sm);
            font-size: var(--fs-xs);
            text-transform: uppercase;
            letter-spacing: var(--ls-overline);
          }

          h3 {
            margin: 0.5rem 0 0 0;
            font-size: var(--type-stat-fs);
          }
        }

        .plan-price {
          text-align: right;

          .price {
            font-size: var(--fs-2xl);
            font-weight: var(--fw-bold);
          }

          .period {
            display: block;
            font-size: var(--fs-xs);
            opacity: 0.8;
          }
        }
      }

      .plan-features {
        padding: 1rem;
        display: grid;
        grid-template-columns: repeat(2, 1fr);
        gap: 0.5rem;

        .feature {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: var(--fs-sm);

          i {
            color: var(--green-500);
          }
        }
      }
    }

    .comparison {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 1rem;
      margin-top: 1rem;
      padding: 1rem;
      background: var(--surface-ground);
      border-radius: var(--radius-lg);

      .pi-arrow-right {
        color: var(--primary-color);
        font-size: var(--type-stat-fs);
      }

      .comparison-item {
        text-align: center;

        .label {
          display: block;
          font-size: var(--fs-xs);
          color: var(--text-color-secondary);
        }

        .value {
          display: block;
          font-weight: var(--fw-semibold);
          font-size: var(--fs-lg);
        }

        &.current .value {
          color: var(--text-color-secondary);
          text-decoration: line-through;
        }

        &.upgrade .value {
          color: var(--accent-text);
        }
      }
    }

    .dialog-footer {
      display: flex;
      justify-content: flex-end;
      gap: 0.75rem;
    }
  `]
})
export class UpgradePlanDialogComponent {
  readonly icons = { CircleCheck, ArrowRight, ArrowUpRight };
  private router = inject(Router);

  @Input() visible = false;
  @Input() data: UpgradeDialogData | null = null;
  
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() upgrade = new EventEmitter<void>();

  /*
   * Estos valores son un RESPALDO: el signal no lo actualiza nadie hoy, así que
   * es lo que el usuario ve cuando choca con un límite de plan. Estaban
   * desincronizados del catálogo real (decían Profesional $399.990, 10
   * usuarios, 200 equipos y 100 contratos/mes; el plan real es Professional
   * $400.000, 5 usuarios, 300 equipos y 80 contratos/mes) y el código iba sin
   * la «s», que no es el que siembra `BootstrapTenantService`.
   *
   * Lo correcto a futuro es leerlos de /planes como ya hace la landing, y no
   * tenerlos escritos acá: un precio hardcodeado envejece solo. Mientras tanto,
   * que al menos diga la verdad — la fuente es CLAUDE.md §9.2.
   */
  planRecomendado = signal<PlanRecomendado>({
    codigo: 'PROFESSIONAL',
    nombre: 'Professional',
    precio: 400000,
    limite: 300,
    features: [
      '5 usuarios',
      '300 equipos',
      '80 contratos/mes',
      'Reportes avanzados',
      'Múltiples bodegas',
      'Soporte prioritario'
    ]
  });

  getHeader(): string {
    return 'Actualiza tu Plan';
  }

  getTitulo(): string {
    const tipo = this.data?.tipoLimite || 'EQUIPOS';
    const titulos: Record<string, string> = {
      'USUARIOS': 'Límite de usuarios alcanzado',
      'EQUIPOS': 'Límite de equipos alcanzado',
      'CONTRATOS': 'Límite de contratos alcanzado',
      'STORAGE': 'Límite de almacenamiento alcanzado',
      'FEATURE': 'Funcionalidad no disponible'
    };
    return titulos[tipo] || 'Límite alcanzado';
  }

  getMensaje(): string {
    if (this.data?.mensaje) return this.data.mensaje;

    const tipo = this.data?.tipoLimite || 'EQUIPOS';
    const mensajes: Record<string, string> = {
      'USUARIOS': 'Necesitas más usuarios para tu equipo de trabajo.',
      'EQUIPOS': 'Has registrado todos los equipos permitidos en tu plan.',
      'CONTRATOS': 'Has alcanzado el límite de contratos mensuales.',
      'STORAGE': 'Has utilizado todo el espacio de almacenamiento.',
      'FEATURE': 'Esta funcionalidad está disponible en planes superiores.'
    };
    return mensajes[tipo] || 'Actualiza tu plan para continuar.';
  }

  getIcon(): string {
    const tipo = this.data?.tipoLimite || 'EQUIPOS';
    const icons: Record<string, string> = {
      'USUARIOS': 'pi pi-users',
      'EQUIPOS': 'pi pi-box',
      'CONTRATOS': 'pi pi-file',
      'STORAGE': 'pi pi-database',
      'FEATURE': 'pi pi-lock'
    };
    return icons[tipo] || 'pi pi-exclamation-triangle';
  }

  getIconClass(): string {
    return (this.data?.tipoLimite || 'equipos').toLowerCase();
  }

  getLimiteLabel(): string {
    const tipo = this.data?.tipoLimite || 'EQUIPOS';
    const labels: Record<string, string> = {
      'USUARIOS': 'usuarios',
      'EQUIPOS': 'equipos',
      'CONTRATOS': 'contratos/mes',
      'STORAGE': 'GB',
      'FEATURE': ''
    };
    return labels[tipo] || '';
  }

  onClose() {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  onUpgrade() {
    this.onClose();
    this.router.navigate(['/configuracion']);
    this.upgrade.emit();
  }
}